import { HvacArea, HvacGroup } from '@belimo-retrofit-portal/logic';
import { constant, pipe } from 'fp-ts/function';
import * as A from 'fp-ts/ReadonlyArray';
import * as R from 'fp-ts/ReadonlyRecord';
import { createSelector } from 'reselect';
import { getConfigSchema } from 'src/modules/config/selectors/getConfigSchema';
import { ConfigSchemaFile, ConfigSchemaRule } from 'src/modules/config/types/ConfigSchema';
import { HVAC_SCHEMA_ANNOTATION_EMISSION } from 'src/modules/hvac/constants/schema';
import { HVAC_VERSION_LIST } from 'src/modules/hvac/constants/version';
import { getHvacAssesmentFormData } from 'src/modules/hvac/selectors/getHvacAssesmentFormData';
import {
  HvacSchemaAnnotation,
  HvacSchemaGroup,
  HvacSchemaLayer,
  HvacSchemaVersion,
} from 'src/modules/hvac/types/HvacSchema';
import { HvacVersion } from 'src/modules/hvac/types/HvacVersion';
import { assertDefined } from 'src/utils/assert';

export const getHvacSchemaEmission = createSelector([
  getHvacAssesmentFormData,
  getConfigSchema,
  constant(HVAC_SCHEMA_ANNOTATION_EMISSION),
], (hvacForm, config, annotations): ReadonlyArray<HvacSchemaGroup> => {
  return hvacForm.groups[HvacGroup.EMISSION].map((groupData): HvacSchemaGroup => {
    const layers: Record<HvacVersion, HvacSchemaLayer[]> = {
      [HvacVersion.ACTUAL]: [],
      [HvacVersion.FUTURE]: [],
    };
    const replacements: Record<HvacVersion, Map<string, ConfigSchemaFile>> = {
      [HvacVersion.ACTUAL]: new Map(),
      [HvacVersion.FUTURE]: new Map(),
    };

    const sectionMap = pipe(
      groupData.areas,
      R.toEntries,
      A.map(([, area]) => area.sections),
      A.flatten,
      A.map((section) => [section.config.id, section] as const),
      R.fromEntries,
    );

    if (
      hvacForm.areas[HvacArea.HEATING].enabled ||
      hvacForm.areas[HvacArea.COOLING].enabled ||
      hvacForm.areas[HvacArea.VENTILATION].enabled
    ) {
      const section12 = sectionMap['12'];
      if (section12?.enabled) {
        for (const version of HVAC_VERSION_LIST) {
          layers[version].push({
            file: getFile('E_GENERAL_EmissionTABSRoom'),
            area: null,
            section: null,
          }, {
            file: getFile('E_GENERAL_BACS'),
            area: null,
            section: null,
          });
        }
      } else {
        for (const version of HVAC_VERSION_LIST) {
          layers[version].push({
            file: getFile('E_GENERAL_EmissionRoom'),
            area: null,
            section: null,
          }, {
            file: getFile('E_GENERAL_BACS'),
            area: null,
            section: null,
          });
        }
      }
    }

    if (
      hvacForm.areas[HvacArea.HEATING].enabled &&
      groupData.areas[HvacArea.HEATING].visible
    ) {
      const section12 = sectionMap['12'];
      if (section12?.enabled) {
        for (const version of HVAC_VERSION_LIST) {
          layers[version].push({
            file: getFile('E_HEATING_Source_TABS'),
            area: HvacArea.HEATING,
            section: null,
          });
        }
      } else {
        for (const version of HVAC_VERSION_LIST) {
          layers[version].push({
            file: getFile('E_HEATING_Source'),
            area: HvacArea.HEATING,
            section: null,
          });
        }
      }
    }
    if (
      hvacForm.areas[HvacArea.COOLING].enabled &&
      groupData.areas[HvacArea.COOLING].visible
    ) {
      const section32 = sectionMap['32'];
      if (section32?.enabled) {
        for (const version of HVAC_VERSION_LIST) {
          layers[version].push({
            file: getFile('E_COOLING_Source_TABS'),
            area: HvacArea.COOLING,
            section: null,
          });
        }
      } else {
        for (const version of HVAC_VERSION_LIST) {
          layers[version].push({
            file: getFile('E_COOLING_Source'),
            area: HvacArea.COOLING,
            section: null,
          });
        }
      }
    }
    if (
      hvacForm.areas[HvacArea.VENTILATION].enabled &&
      groupData.areas[HvacArea.VENTILATION].visible
    ) {
      for (const version of HVAC_VERSION_LIST) {
        const hasAnswer = groupData.areas[HvacArea.VENTILATION].sections.some((section) => (
          section.enabled &&
          section.answer[version].option !== null
        ));
        if (!hasAnswer) {
          continue;
        }

        layers[version].push({
          file: getFile('E_VENTILATION_Ducts'),
          area: HvacArea.VENTILATION,
          section: null,
        });
      }
    }

    for (const version of HVAC_VERSION_LIST) {
      for (const rule of config[HvacGroup.EMISSION].rules) {
        const section = assertDefined(
          sectionMap[rule.section]?.config,
          `Section "${rule.section}" could not be found`,
          { rule, hvacForm },
        );

        for (const matchedRule of matchRule(rule, version)) {
          if (groupData.areas[matchedRule.area].visible) {
            for (const file of matchedRule.layers) {
              layers[version].push({
                file: getFile(file),
                area: null,
                section: section,
              });
            }
          }
          if (matchedRule.replaces) {
            Object.entries(matchedRule.replaces).forEach(([oldFile, newFile]) => {
              replacements[version].set(oldFile, getFile(newFile));
            });
          }
        }
      }
    }

    return pipe(
      layers,
      R.mapWithIndex((version, versionLayers) => mapGroup(
        applyReplacements(versionLayers, replacements[version]),
        groupData.annotations[version],
      )),
    );

    function matchRule(
      rule: ConfigSchemaRule,
      version: HvacVersion,
    ): ConfigSchemaRule[] {
      if (!hvacForm.areas[rule.area].enabled) {
        return [];
      }

      const section = sectionMap[rule.section] ?? null;
      if (!section || !section.enabled) {
        return [];
      }

      const answer = section.answer[version].option;
      if (!answer || answer.id !== rule.answer) {
        return [];
      }

      return rule.rules
        ? rule.rules.map((subRule) => matchRule(subRule, version)).flat(1).concat(rule)
        : [rule];
    }
  });

  function mapGroup(
    layers: ReadonlyArray<HvacSchemaLayer>,
    texts: R.ReadonlyRecord<string, string>,
  ): HvacSchemaVersion {
    return {
      layers: layers,
      annotations: mapAnnotations(layers, texts),
    };
  }

  function mapAnnotations(
    layers: ReadonlyArray<HvacSchemaLayer>,
    texts: R.ReadonlyRecord<string, string>,
  ): HvacSchemaAnnotation[] {
    return annotations
      .filter((annotation) => layers.some((it) => annotation.files.has(it.file.fileName)))
      .map((annotation) => ({ config: annotation, text: texts[annotation.key] ?? '' }));
  }

  function getFile(fileName: string): ConfigSchemaFile {
    return assertDefined(
      config[HvacGroup.EMISSION].files.find((it) => it.fileName === fileName),
      `File "${fileName}" does not exist`,
      { config },
    );
  }

  function applyReplacements(
    layers: ReadonlyArray<HvacSchemaLayer>,
    replacements: ReadonlyMap<string, ConfigSchemaFile>,
  ): HvacSchemaLayer[] {
    return layers.map((layer) => {
      const replacement = replacements.get(layer.file.fileName);
      return replacement
        ? { ...layer, file: replacement }
        : layer;
    });
  }
});
